import React, { useEffect, useState, useRef } from "react";
import { createPortal } from "react-dom";
import { Sheet, SheetContent, SheetHeader, SheetTitle } from "./ui/sheet";
import api, { inr } from "../lib/api";
import { toast } from "sonner";
import { useAuth } from "../lib/auth";
import { User, MapPin, CreditCard, Package, Clock, Mail, Phone, Truck, ShieldCheck, Printer, Download, Pencil, RotateCcw, History, ClipboardCheck, Camera, Check, UserPlus } from "lucide-react";
import jsQR from "jsqr";

const PAYMENT_PILL = {
  paid: "bg-emerald-50 text-emerald-700 border-emerald-200",
  created: "bg-amber-50 text-amber-700 border-amber-200",
  cod: "bg-sky-50 text-sky-700 border-sky-200",
  cancelled: "bg-rose-50 text-rose-700 border-rose-200",
};
const PAYMENT_LABEL = { paid: "Paid", created: "Pending", cod: "COD", cancelled: "Cancelled" };

const ORDER_STATUSES = ["pending", "processing", "ready_for_dispatch", "shipped", "delivered", "cancelled"];
const LEGACY_STATUS_MAP = { placed: "pending", confirmed: "processing", out_for_delivery: "shipped" };
const normalizeOrderStatus = (s) => LEGACY_STATUS_MAP[s] || s || "pending";

const derivePaymentStatus = (o) => {
  if (o.status === "cancelled" || o.tracking_status === "cancelled") return "cancelled";
  if (o.payment_method === "cod") return "cod";
  return o.status === "paid" ? "paid" : "created";
};

const fmtDate = (iso) => {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleString("en-IN", { day: "numeric", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" });
  } catch { return iso; }
};

const ACTION_LABEL = { address_edit: "Address edited", cancel: "Order cancelled", refund: "Refund issued" };

// Mirrors FULFILMENT_STEPS / FULFILMENT_STEP_LABELS in server.py
const FULFILMENT_STEPS = [
  { key: "invoice_checked", label: "Checked against invoice" },
  { key: "item_picked", label: "Retrieved from inventory" },
  { key: "qr_scanned", label: "QR scanned, stock updated" },
  { key: "photo_taken", label: "Photo taken as proof" },
  { key: "packed", label: "Packed in box" },
  { key: "sticker_attached", label: "Shipment sticker attached" },
];

const Section = ({ icon: Icon, title, children, testid, action }) => (
  <section data-testid={testid} className="space-y-3">
    <div className="flex items-center justify-between">
      <div className="flex items-center gap-2 text-xs uppercase tracking-[0.2em] text-welljio-subtle font-semibold">
        <Icon size={14} strokeWidth={2.2} />
        <span>{title}</span>
      </div>
      {action}
    </div>
    <div className="bg-welljio-tint/40 border border-slate-100 rounded-2xl p-4 text-sm">
      {children}
    </div>
  </section>
);

const Field = ({ label, value }) => (
  <div className="flex items-start gap-3 py-1.5">
    <span className="w-28 shrink-0 text-xs uppercase tracking-wide text-welljio-muted">{label}</span>
    <span className="text-welljio-ink break-all">{value || "—"}</span>
  </div>
);

const VerifyFields = ({ verifiedVia, setVerifiedVia, reason, setReason, contactHint, testidPrefix }) => (
  <div className="space-y-2 mt-3 pt-3 border-t border-slate-200">
    <p className="text-xs text-welljio-muted">Confirm this request matches the contact on file: {contactHint}</p>
    <input
      value={verifiedVia}
      onChange={(e) => setVerifiedVia(e.target.value)}
      placeholder="Email or phone the customer used to request this"
      data-testid={`${testidPrefix}-verified-via`}
      className="w-full border border-slate-200 rounded-xl px-3.5 py-2.5 text-sm outline-none focus:border-welljio-accent"
    />
    <textarea
      value={reason}
      onChange={(e) => setReason(e.target.value)}
      placeholder="Reason"
      rows={2}
      data-testid={`${testidPrefix}-reason`}
      className="w-full border border-slate-200 rounded-xl px-3.5 py-2.5 text-sm outline-none focus:border-welljio-accent"
    />
  </div>
);

export default function OrderDetailsDrawer({ order, open, onOpenChange, onStatusChange, onOrderUpdate }) {
  const { user, can } = useAuth();
  const canManageOrder = can("orders.edit_cancel_refund");
  const canFulfil = can("orders.fulfil") || canManageOrder;

  const [editingAddress, setEditingAddress] = useState(false);
  const [addressForm, setAddressForm] = useState({ address_line1: "", address_line2: "", city: "", state: "", pincode: "" });
  const [addressVerifiedVia, setAddressVerifiedVia] = useState("");
  const [addressReason, setAddressReason] = useState("");
  const [savingAddress, setSavingAddress] = useState(false);

  const [showRefundForm, setShowRefundForm] = useState(false);
  const [refundAmount, setRefundAmount] = useState("");
  const [refundVerifiedVia, setRefundVerifiedVia] = useState("");
  const [refundReason, setRefundReason] = useState("");
  const [refunding, setRefunding] = useState(false);

  const [activityLog, setActivityLog] = useState([]);
  const [loadingLog, setLoadingLog] = useState(false);

  const [claiming, setClaiming] = useState(false);
  const [submittingStep, setSubmittingStep] = useState(null);
  const [showReassign, setShowReassign] = useState(false);
  const [reassignEmail, setReassignEmail] = useState("");
  const [reassigning, setReassigning] = useState(false);
  const [scanBarcode, setScanBarcode] = useState("");
  const [scanning, setScanning] = useState(false);
  const [cameraOpen, setCameraOpen] = useState(false);
  const [cameraError, setCameraError] = useState("");
  const [capturedPhotos, setCapturedPhotos] = useState([]); // [{ blob, url }] — pending capture, not yet uploaded
  const [photoCameraOpen, setPhotoCameraOpen] = useState(false);
  const [photoCameraError, setPhotoCameraError] = useState("");
  const [lightboxUrl, setLightboxUrl] = useState(null);
  const [lightboxIndex, setLightboxIndex] = useState(0);
  const scanInputRef = useRef(null);
  const videoRef = useRef(null);
  const streamRef = useRef(null);
  const photoVideoRef = useRef(null);
  const photoStreamRef = useRef(null);
  const scanLoopRef = useRef(null);
  // Guards against double-submits inside the camera's detection loop — a
  // ref, not state, because that loop runs via requestAnimationFrame and a
  // captured state value there would be stale.
  const isSubmittingRef = useRef(false);

  // Stop the camera if the drawer closes or unmounts — never leave it
  // silently running in the background. Uses only the refs directly
  // (not the closeCamera function, which is defined further down, after
  // the early return below — referencing it here would be undefined on
  // any render where `order` is null).
  useEffect(() => {
    if (!open) {
      if (scanLoopRef.current) cancelAnimationFrame(scanLoopRef.current);
      if (streamRef.current) streamRef.current.getTracks().forEach((t) => t.stop());
      if (photoStreamRef.current) photoStreamRef.current.getTracks().forEach((t) => t.stop());
    }
    return () => {
      if (scanLoopRef.current) cancelAnimationFrame(scanLoopRef.current);
      if (streamRef.current) streamRef.current.getTracks().forEach((t) => t.stop());
      if (photoStreamRef.current) photoStreamRef.current.getTracks().forEach((t) => t.stop());
    };
  }, [open]);

  useEffect(() => {
    setEditingAddress(false);
    setShowRefundForm(false);
    setShowReassign(false);
    setActivityLog([]);
    setCapturedPhotos([]);
    setPhotoCameraOpen(false);
    setPhotoCameraError("");
    setLightboxUrl(null);
    setLightboxIndex(0);
    if (order && open && canManageOrder) {
      setLoadingLog(true);
      api.get(`/admin/orders/${order.id}/activity-log`)
        .then(({ data }) => setActivityLog(data))
        .catch(() => {})
        .finally(() => setLoadingLog(false));
    }
  }, [order?.id, open, canManageOrder]);

  if (!order) return null;
  const pay = derivePaymentStatus(order);
  const addr = order.shipping_address || {};
  const items = order.items || [];
  const history = order.status_history || [];
  const currentStatus = normalizeOrderStatus(order.tracking_status);
  const contactHint = order.customer?.email || order.customer?.phone || "—";
  const refundedAmount = order.refunded_amount || 0;
  const refundRemaining = Math.max(0, (order.total || 0) - refundedAmount);

  const isClaimed = !!order.claimed_by_id;
  const isClaimant = order.claimed_by_id === user?.id;
  const doneSteps = (order.fulfilment_steps || []).map((s) => s.step);
  const nextStepIndex = FULFILMENT_STEPS.findIndex((s) => !doneSteps.includes(s.key));
  const allStepsDone = nextStepIndex === -1;
  // Real physical scans still needed before qr_scanned can complete — items
  // with no resolved variant (not yet migrated) never contribute here, so
  // an order made entirely of those can still complete via mark-done.
  const outstandingScans = (order.items || []).reduce((sum, it) => sum + (it.remaining_to_scan || 0), 0);
  const isScanStep = FULFILMENT_STEPS[nextStepIndex]?.key === "qr_scanned";
  const isPhotoStep = FULFILMENT_STEPS[nextStepIndex]?.key === "photo_taken";
  const showFulfilmentSection = canFulfil && !["shipped", "delivered", "cancelled"].includes(order.tracking_status);
  // Mirrors the backend's _collect_order_photos — reads both the legacy
  // singular photo_url (entries logged before multi-photo support) and the
  // current photo_urls list, so nothing already in production is hidden.
  // Independent of showFulfilmentSection so it still renders after
  // shipped/delivered/cancelled, when a dispute is most likely to happen.
  const packingPhotoUrls = (order.fulfilment_steps || [])
    .filter((s) => s.step === "photo_taken")
    .flatMap((s) => [...(s.photo_url ? [s.photo_url] : []), ...(s.photo_urls || [])]);

  const openAddressEdit = () => {
    setAddressForm({ address_line1: addr.line1 || "", address_line2: addr.line2 || "", city: addr.city || "", state: addr.state || "", pincode: addr.pincode || "" });
    setAddressVerifiedVia("");
    setAddressReason("");
    setEditingAddress(true);
  };

  const saveAddress = async () => {
    if (!addressVerifiedVia.trim() || !addressReason.trim()) {
      toast.error("Enter both a verification contact and a reason");
      return;
    }
    setSavingAddress(true);
    try {
      const { data } = await api.patch(`/admin/orders/${order.id}/address`, { ...addressForm, verified_via: addressVerifiedVia.trim(), reason: addressReason.trim() });
      toast.success("Address updated");
      onOrderUpdate?.(data);
      setEditingAddress(false);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not update address");
    } finally {
      setSavingAddress(false);
    }
  };

  const submitRefund = async () => {
    if (!refundVerifiedVia.trim() || !refundReason.trim()) {
      toast.error("Enter both a verification contact and a reason");
      return;
    }
    setRefunding(true);
    try {
      const { data } = await api.post(`/admin/orders/${order.id}/refund`, { amount: refundAmount ? parseFloat(refundAmount) : null, verified_via: refundVerifiedVia.trim(), reason: refundReason.trim() });
      toast.success("Refund issued");
      onOrderUpdate?.(data);
      setShowRefundForm(false);
      setRefundAmount(""); setRefundVerifiedVia(""); setRefundReason("");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not issue refund");
    } finally {
      setRefunding(false);
    }
  };

  const claimOrder = async () => {
    setClaiming(true);
    try {
      const { data } = await api.post(`/admin/orders/${order.id}/claim`);
      toast.success("Order claimed");
      onOrderUpdate?.(data);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Couldn't claim this order");
    } finally {
      setClaiming(false);
    }
  };

  const submitReassign = async () => {
    if (!reassignEmail.trim()) {
      toast.error("Enter the email of who you're reassigning to");
      return;
    }
    setReassigning(true);
    try {
      const { data } = await api.post(`/admin/orders/${order.id}/reassign`, { new_claimant_email: reassignEmail.trim() });
      toast.success(`Reassigned to ${data.claimed_by_name}`);
      onOrderUpdate?.(data);
      setShowReassign(false);
      setReassignEmail("");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Couldn't reassign this order");
    } finally {
      setReassigning(false);
    }
  };

  const submitStep = async (step, file) => {
    setSubmittingStep(step);
    try {
      const formData = new FormData();
      formData.append("step", step);
      if (file) formData.append("photo", file);
      const { data } = await api.post(`/admin/orders/${order.id}/fulfilment-step`, formData, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success("Step logged");
      onOrderUpdate?.(data);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Couldn't log this step");
    } finally {
      setSubmittingStep(null);
    }
  };

  const printShipmentSticker = async () => {
    try {
      // Same pattern as the variant sticker — a plain <a href> link can't
      // carry the login token, so fetch through the authenticated api
      // instance and open the result as a local blob instead.
      const response = await api.get(`/admin/orders/${order.id}/sticker`, { responseType: "blob" });
      const blobUrl = URL.createObjectURL(new Blob([response.data], { type: "application/pdf" }));
      window.open(blobUrl, "_blank");
    } catch (err) {
      let detail = "Couldn't generate the shipment sticker";
      if (err.response?.data instanceof Blob) {
        try {
          const parsed = JSON.parse(await err.response.data.text());
          detail = parsed.detail || detail;
        } catch { /* body wasn't JSON — keep the generic message */ }
      }
      toast.error(detail);
    }
  };

  const openPhotoCamera = async () => {
    setPhotoCameraError("");
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ video: { facingMode: "environment" } });
      photoStreamRef.current = stream;
      setPhotoCameraOpen(true);
      setTimeout(() => {
        if (photoVideoRef.current) {
          photoVideoRef.current.srcObject = stream;
          photoVideoRef.current.play();
        }
      }, 50);
    } catch (err) {
      setPhotoCameraError("Couldn't access the camera — check browser permissions.");
    }
  };

  // Stops the preview only — captured-but-unsubmitted photos are kept,
  // same as the QR scanner's "Stop camera" not erasing scan progress.
  const stopPhotoCamera = () => {
    if (photoStreamRef.current) {
      photoStreamRef.current.getTracks().forEach((t) => t.stop());
      photoStreamRef.current = null;
    }
    setPhotoCameraOpen(false);
  };

  const capturePhotoFrame = () => {
    const video = photoVideoRef.current;
    if (!video || !video.videoWidth) return;
    const canvas = document.createElement("canvas");
    canvas.width = video.videoWidth;
    canvas.height = video.videoHeight;
    canvas.getContext("2d").drawImage(video, 0, 0, canvas.width, canvas.height);
    canvas.toBlob((blob) => {
      if (!blob) return;
      setCapturedPhotos((prev) => [...prev, { blob, url: URL.createObjectURL(blob) }]);
    }, "image/jpeg", 0.9);
  };

  const removeCapturedPhoto = (idx) => {
    setCapturedPhotos((prev) => {
      const removed = prev[idx];
      if (removed) URL.revokeObjectURL(removed.url);
      return prev.filter((_, i) => i !== idx);
    });
  };

  const submitCapturedPhotos = async () => {
    if (capturedPhotos.length === 0) return;
    setSubmittingStep("photo_taken");
    try {
      const formData = new FormData();
      formData.append("step", "photo_taken");
      capturedPhotos.forEach((p, i) => formData.append("photos", p.blob, `photo-${i}.jpg`));
      const { data } = await api.post(`/admin/orders/${order.id}/fulfilment-step`, formData, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success(`${capturedPhotos.length} photo${capturedPhotos.length > 1 ? "s" : ""} logged`);
      onOrderUpdate?.(data);
      stopPhotoCamera();
      capturedPhotos.forEach((p) => URL.revokeObjectURL(p.url));
      setCapturedPhotos([]);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Couldn't upload these photos");
    } finally {
      setSubmittingStep(null);
    }
  };

  const downloadPhoto = async (url, idx = 0) => {
    try {
      // Same reasoning as printShipmentSticker above: fetch through the
      // api instance and save via a local blob URL rather than trusting a
      // plain <a href> to the raw path. url comes straight from the
      // backend as "/api/files/..." (correct for a plain <img src>), but
      // the api instance's baseURL already ends in /api, so passing it
      // through unchanged doubles up into /api/api/files/... and 404s.
      const path = url.replace(/^\/api/, "");
      const response = await api.get(path, { responseType: "blob" });
      const blobUrl = URL.createObjectURL(response.data);
      const a = document.createElement("a");
      a.href = blobUrl;
      a.download = `packing-photo-${idx + 1}.jpg`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(blobUrl);
    } catch (err) {
      toast.error("Couldn't download this photo");
    }
  };

  const submitScan = async (e) => {
    e.preventDefault();
    if (!scanBarcode.trim()) return;
    setScanning(true);
    try {
      const { data } = await api.post(`/admin/orders/${order.id}/scan-item`, { barcode: scanBarcode.trim() });
      toast.success(
        data.remaining_for_item > 0
          ? `${data.scanned_item} scanned — ${data.remaining_for_item} more needed`
          : `${data.scanned_item} — all units scanned`
      );
      onOrderUpdate?.(data.order);
      setScanBarcode("");
      scanInputRef.current?.focus();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Couldn't scan this item");
      setScanBarcode("");
      scanInputRef.current?.focus();
    } finally {
      setScanning(false);
    }
  };

  const submitDetectedBarcode = async (code) => {
    if (isSubmittingRef.current) return;
    isSubmittingRef.current = true;
    setScanning(true);
    try {
      const { data } = await api.post(`/admin/orders/${order.id}/scan-item`, { barcode: code });
      toast.success(
        data.remaining_for_item > 0
          ? `${data.scanned_item} scanned — ${data.remaining_for_item} more needed`
          : `${data.scanned_item} — all units scanned`
      );
      onOrderUpdate?.(data.order);
      if (data.outstanding_total === 0) closeCamera();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Couldn't scan this item");
    } finally {
      isSubmittingRef.current = false;
      setScanning(false);
    }
  };

  const startScanLoop = () => {
    const canvas = document.createElement("canvas");
    const ctx = canvas.getContext("2d", { willReadFrequently: true });
    let lastAttempt = 0;
    const loop = () => {
      const video = videoRef.current;
      if (!video || !streamRef.current) return;
      const now = Date.now();
      // Throttle detection attempts — no point decoding every single frame,
      // and it avoids re-triggering on the same still-visible code the
      // instant a submission finishes.
      if (!isSubmittingRef.current && now - lastAttempt > 400 && video.readyState === video.HAVE_ENOUGH_DATA) {
        lastAttempt = now;
        canvas.width = video.videoWidth;
        canvas.height = video.videoHeight;
        ctx.drawImage(video, 0, 0, canvas.width, canvas.height);
        const imageData = ctx.getImageData(0, 0, canvas.width, canvas.height);
        const result = jsQR(imageData.data, imageData.width, imageData.height);
        if (result?.data) submitDetectedBarcode(result.data);
      }
      scanLoopRef.current = requestAnimationFrame(loop);
    };
    scanLoopRef.current = requestAnimationFrame(loop);
  };

  const openCamera = async () => {
    setCameraError("");
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ video: { facingMode: "environment" } });
      streamRef.current = stream;
      setCameraOpen(true);
      setTimeout(() => {
        if (videoRef.current) {
          videoRef.current.srcObject = stream;
          videoRef.current.play();
          startScanLoop();
        }
      }, 50);
    } catch (err) {
      setCameraError("Couldn't access the camera — check browser permissions, or use manual entry below.");
    }
  };

  const closeCamera = () => {
    if (scanLoopRef.current) {
      cancelAnimationFrame(scanLoopRef.current);
      scanLoopRef.current = null;
    }
    if (streamRef.current) {
      streamRef.current.getTracks().forEach((t) => t.stop());
      streamRef.current = null;
    }
    setCameraOpen(false);
  };

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent side="right" data-testid="order-details-drawer" className="w-full sm:max-w-xl overflow-y-auto bg-white p-0">
        <SheetHeader className="sticky top-0 z-10 bg-white/95 backdrop-blur-md border-b border-slate-100 px-6 py-5">
          <div className="flex items-start justify-between gap-4">
            <div>
              <p className="text-[10px] uppercase tracking-[0.28em] text-welljio-subtle mb-1">Order</p>
              <SheetTitle className="font-heading text-2xl font-bold tracking-tight text-welljio-ink">#{order.id.slice(-10)}</SheetTitle>
              <p className="text-xs text-welljio-muted mt-1">Placed {fmtDate(order.created_at)}</p>
            </div>
            <div className="flex flex-col items-end gap-1.5 shrink-0">
              <span data-testid="drawer-payment-pill" className={`inline-flex items-center gap-1.5 rounded-full border px-2.5 py-1 text-[10px] font-semibold uppercase tracking-wide ${PAYMENT_PILL[pay]}`}>
                <span className="w-1.5 h-1.5 rounded-full bg-current" />
                {PAYMENT_LABEL[pay]}
              </span>
              <span data-testid="drawer-order-pill" className="inline-flex items-center gap-1.5 rounded-full border border-welljio-ink/15 bg-white px-2.5 py-1 text-[10px] font-semibold uppercase tracking-wide text-welljio-accent capitalize">
                <Truck size={10} strokeWidth={2.4} />
                {currentStatus.replace(/_/g, " ")}
              </span>
            </div>
          </div>
        </SheetHeader>

        <div className="px-6 py-6 space-y-6">
          {/* Invoice actions */}
          <div className="flex items-center gap-3 flex-wrap" data-testid="drawer-invoice-actions">
            <a href={`/invoice/${order.id}?autoprint=1`} target="_blank" rel="noreferrer" data-testid="drawer-print-invoice" className="inline-flex items-center gap-2 rounded-full border border-welljio-ink/15 bg-white px-4 py-2 text-xs font-medium hover:bg-welljio-tint transition-colors">
              <Printer size={14} strokeWidth={2.2} /> Print Invoice
            </a>
            <a href={`/invoice/${order.id}?autoprint=1`} target="_blank" rel="noreferrer" data-testid="drawer-download-invoice" className="inline-flex items-center gap-2 rounded-full bg-welljio-accent text-white px-4 py-2 text-xs font-medium hover:opacity-90 transition-opacity">
              <Download size={14} strokeWidth={2.2} /> Download PDF
            </a>
            <a href={`/invoice/${order.id}`} target="_blank" rel="noreferrer" data-testid="drawer-view-invoice" className="text-xs text-welljio-accent hover:underline">View invoice ↗</a>
          </div>

          {/* Order Status Update */}
          {onStatusChange && (
            <section data-testid="drawer-status-updater" className="bg-welljio-tint/60 border border-welljio-accent/15 rounded-2xl p-4">
              <div className="flex items-center justify-between gap-4 flex-wrap">
                <div>
                  <p className="text-xs uppercase tracking-[0.2em] text-welljio-subtle font-semibold mb-1">Update Order Status</p>
                  <p className="text-xs text-welljio-muted">Changes here are logged to the timeline instantly.</p>
                </div>
                <select
                  data-testid="drawer-status-select"
                  value={currentStatus}
                  onChange={(e) => onStatusChange(order.id, e.target.value)}
                  className="rounded-full border border-welljio-accent/30 bg-white px-4 py-2 text-sm capitalize font-medium text-welljio-ink focus:outline-none focus:ring-2 focus:ring-welljio-accent/30"
                >
                  {ORDER_STATUSES.map((s) => <option key={s} value={s} className="capitalize">{s.replace(/_/g, " ")}</option>)}
                </select>
              </div>
            </section>
          )}

          {/* Fulfilment Checklist — Fulfilment Phase */}
          {showFulfilmentSection && (
            <Section
              icon={ClipboardCheck}
              title="Fulfilment Checklist"
              testid="drawer-fulfilment-section"
              action={isClaimed && canManageOrder && order.tracking_status !== "ready_for_dispatch" && (
                <button onClick={() => setShowReassign((v) => !v)} data-testid="drawer-reassign-toggle" className="inline-flex items-center gap-1.5 text-xs text-welljio-accent hover:underline">
                  <UserPlus size={12} /> Reassign
                </button>
              )}
            >
              {!isClaimed ? (
                <div className="flex items-center justify-between">
                  <p className="text-welljio-muted">Not yet claimed.</p>
                  <button onClick={claimOrder} disabled={claiming} data-testid="drawer-claim-button" className="bg-welljio-ink text-white rounded-full px-4 py-2 text-xs font-medium hover:bg-slate-800 disabled:opacity-60">
                    {claiming ? "Claiming..." : "Claim this order"}
                  </button>
                </div>
              ) : (
                <>
                  <p className="text-welljio-ink mb-3">
                    Claimed by <span className="font-medium">{order.claimed_by_name}</span>
                    {order.claimed_at && <span className="text-welljio-subtle"> · {fmtDate(order.claimed_at)}</span>}
                  </p>

                  {showReassign && (
                    <div className="flex items-center gap-2 mb-3 pb-3 border-b border-slate-200">
                      <input
                        value={reassignEmail}
                        onChange={(e) => setReassignEmail(e.target.value)}
                        placeholder="Reassign to (staff email)"
                        data-testid="drawer-reassign-email"
                        className="flex-1 border border-slate-200 rounded-xl px-3.5 py-2 text-sm outline-none focus:border-welljio-accent"
                      />
                      <button onClick={submitReassign} disabled={reassigning} data-testid="drawer-reassign-submit" className="bg-welljio-ink text-white rounded-full px-4 py-2 text-xs font-medium hover:bg-slate-800 disabled:opacity-60">
                        {reassigning ? "..." : "Reassign"}
                      </button>
                    </div>
                  )}

                  <ul className="space-y-2">
                    {FULFILMENT_STEPS.map((step, i) => {
                      const done = doneSteps.includes(step.key);
                      const isNext = i === nextStepIndex;
                      const stepEntry = (order.fulfilment_steps || []).find((s) => s.step === step.key);
                      return (
                        <li key={step.key} className={`flex items-center gap-3 py-1.5 ${!done && !isNext ? "opacity-40" : ""}`}>
                          <span className={`w-5 h-5 rounded-full flex items-center justify-center shrink-0 ${done ? "bg-welljio-ink text-white" : "border border-welljio-subtle"}`}>
                            {done && <Check size={12} strokeWidth={3} />}
                          </span>
                          <div className="flex-1 min-w-0">
                            <p className={done ? "text-welljio-ink" : "text-welljio-muted"}>{step.label}</p>
                            {done && stepEntry && <p className="text-xs text-welljio-subtle">{stepEntry.actor_name} · {fmtDate(stepEntry.timestamp)}</p>}
                          </div>
                          {isNext && isClaimant && (
                            step.key === "photo_taken" ? (
                              <span className="text-xs text-welljio-coral font-medium">Capture below ↓</span>
                            ) : step.key === "qr_scanned" && outstandingScans > 0 ? (
                              <span className="text-xs text-welljio-coral font-medium">{outstandingScans} to scan ↓</span>
                            ) : step.key === "sticker_attached" ? (
                              <div className="flex items-center gap-2">
                                <button onClick={printShipmentSticker} data-testid="drawer-print-shipment-sticker" className="inline-flex items-center gap-1.5 text-xs rounded-full border border-slate-200 px-3 py-1.5 hover:bg-welljio-tint">
                                  <Printer size={12} /> Print sticker
                                </button>
                                <button onClick={() => submitStep(step.key)} disabled={submittingStep === step.key} data-testid={`drawer-step-${step.key}`} className="text-xs rounded-full border border-slate-200 px-3 py-1.5 hover:bg-welljio-tint disabled:opacity-60">
                                  {submittingStep === step.key ? "..." : "Mark done"}
                                </button>
                              </div>
                            ) : (
                              <button onClick={() => submitStep(step.key)} disabled={submittingStep === step.key} data-testid={`drawer-step-${step.key}`} className="text-xs rounded-full border border-slate-200 px-3 py-1.5 hover:bg-welljio-tint disabled:opacity-60">
                                {submittingStep === step.key ? "..." : "Mark done"}
                              </button>
                            )
                          )}
                        </li>
                      );
                    })}
                  </ul>

                  {isClaimant && isPhotoStep && (
                    <div className="mt-3 pt-3 border-t border-slate-200">
                      <p className="text-xs font-semibold text-welljio-ink mb-2">Capture as many angles as needed — for packing proof and dispute protection</p>

                      {capturedPhotos.length > 0 && (
                        <div className="flex flex-wrap gap-2 mb-3">
                          {capturedPhotos.map((p, idx) => (
                            <div key={idx} className="relative w-16 h-16 rounded-lg overflow-hidden border border-slate-200">
                              <img src={p.url} alt={`Capture ${idx + 1}`} className="w-full h-full object-cover" />
                              <button
                                onClick={() => removeCapturedPhoto(idx)}
                                data-testid={`drawer-photo-remove-${idx}`}
                                aria-label="Remove this photo"
                                className="absolute top-0.5 right-0.5 bg-black/60 text-white rounded-full w-4 h-4 flex items-center justify-center text-[10px] leading-none"
                              >
                                ×
                              </button>
                            </div>
                          ))}
                        </div>
                      )}

                      {photoCameraOpen ? (
                        <div className="mb-3">
                          <div className="relative rounded-xl overflow-hidden bg-black">
                            <video ref={photoVideoRef} data-testid="drawer-photo-video" className="w-full aspect-video object-cover" playsInline muted />
                          </div>
                          <div className="flex items-center gap-2 mt-2">
                            <button onClick={capturePhotoFrame} data-testid="drawer-photo-capture" className="flex-1 inline-flex items-center justify-center gap-1.5 bg-welljio-ink text-white rounded-full py-2.5 text-xs font-medium hover:bg-slate-800">
                              <Camera size={13} /> Capture
                            </button>
                            <button onClick={stopPhotoCamera} data-testid="drawer-photo-camera-stop" className="text-xs rounded-full border border-slate-200 px-3 py-2.5 hover:bg-welljio-tint">
                              Stop camera
                            </button>
                          </div>
                        </div>
                      ) : (
                        <button
                          onClick={openPhotoCamera}
                          data-testid="drawer-photo-camera-start"
                          className="w-full mb-3 inline-flex items-center justify-center gap-1.5 bg-welljio-ink text-white rounded-full py-2.5 text-xs font-medium hover:bg-slate-800"
                        >
                          <Camera size={13} /> {capturedPhotos.length > 0 ? "Add another angle" : "Take photo"}
                        </button>
                      )}

                      {photoCameraError && <p className="text-[10px] text-welljio-coral mb-2">{photoCameraError}</p>}

                      {capturedPhotos.length > 0 && (
                        <button
                          onClick={submitCapturedPhotos}
                          disabled={submittingStep === "photo_taken"}
                          data-testid="drawer-photo-submit"
                          className="w-full bg-welljio-accent text-white rounded-full py-2.5 text-xs font-medium hover:opacity-90 disabled:opacity-60"
                        >
                          {submittingStep === "photo_taken" ? "Uploading..." : `Done — Submit ${capturedPhotos.length} Photo${capturedPhotos.length > 1 ? "s" : ""}`}
                        </button>
                      )}
                    </div>
                  )}

                  {isClaimant && isScanStep && outstandingScans > 0 && (
                    <div className="mt-3 pt-3 border-t border-slate-200">
                      <p className="text-xs font-semibold text-welljio-ink mb-2">Scan each item — one scan per unit</p>
                      <ul className="space-y-1 mb-3">
                        {(order.items || []).filter((it) => it.remaining_to_scan > 0).map((it, idx) => (
                          <li key={idx} className="flex items-center justify-between text-xs text-welljio-muted">
                            <span>{it.name}</span>
                            <span className="font-medium text-welljio-coral">{it.remaining_to_scan} left</span>
                          </li>
                        ))}
                      </ul>

                      {cameraOpen ? (
                        <div className="mb-3">
                          <div className="relative rounded-xl overflow-hidden bg-black">
                            <video ref={videoRef} data-testid="drawer-camera-video" className="w-full aspect-video object-cover" playsInline muted />
                            <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
                              <div className="w-2/3 aspect-square border-2 border-white/70 rounded-xl" />
                            </div>
                            {scanning && (
                              <div className="absolute inset-0 bg-black/40 flex items-center justify-center">
                                <span className="text-white text-xs font-medium">Scanning...</span>
                              </div>
                            )}
                          </div>
                          <button onClick={closeCamera} data-testid="drawer-camera-stop" className="w-full mt-2 text-xs rounded-full border border-slate-200 px-3 py-2 hover:bg-welljio-tint">
                            Stop camera
                          </button>
                        </div>
                      ) : (
                        <button
                          onClick={openCamera}
                          data-testid="drawer-camera-start"
                          className="w-full mb-3 inline-flex items-center justify-center gap-1.5 bg-welljio-ink text-white rounded-full py-2.5 text-xs font-medium hover:bg-slate-800"
                        >
                          <Camera size={13} /> Scan with camera
                        </button>
                      )}

                      {cameraError && <p className="text-[10px] text-welljio-coral mb-2">{cameraError}</p>}

                      <form onSubmit={submitScan} className="flex items-center gap-2">
                        <input
                          ref={scanInputRef}
                          value={scanBarcode}
                          onChange={(e) => setScanBarcode(e.target.value)}
                          placeholder="Scan or type barcode"
                          data-testid="drawer-scan-input"
                          className="flex-1 border border-slate-200 rounded-xl px-3.5 py-2.5 text-sm outline-none focus:border-welljio-accent font-mono"
                        />
                        <button type="submit" disabled={scanning || !scanBarcode.trim()} data-testid="drawer-scan-submit" className="bg-welljio-ink text-white rounded-full px-4 py-2.5 text-xs font-medium hover:bg-slate-800 disabled:opacity-60">
                          {scanning ? "..." : "Scan"}
                        </button>
                      </form>
                    </div>
                  )}

                  {allStepsDone && isClaimant && order.tracking_status !== "ready_for_dispatch" && (
                    <button
                      onClick={() => onStatusChange?.(order.id, "ready_for_dispatch")}
                      data-testid="drawer-mark-ready"
                      className="w-full mt-4 bg-welljio-ink text-white rounded-full py-3 text-sm font-medium hover:bg-slate-800"
                    >
                      Mark Ready for Dispatch
                    </button>
                  )}
                  {isClaimed && !isClaimant && (
                    <p className="text-xs text-welljio-subtle mt-3 pt-3 border-t border-slate-200">Only {order.claimed_by_name} can log steps on this order.</p>
                  )}
                </>
              )}
            </Section>
          )}

          {/* Packing Photos — independent of showFulfilmentSection so this
              stays visible after shipped/delivered/cancelled, which is
              exactly when a customer dispute is most likely to happen. */}
          {packingPhotoUrls.length > 0 && (
            <Section icon={Camera} title={`Packing Photos (${packingPhotoUrls.length})`} testid="drawer-packing-photos-section">
              <p className="text-xs text-welljio-muted mb-3">Captured as proof during packing — retained for a limited window for dispute resolution.</p>
              <div className="grid grid-cols-4 gap-2">
                {packingPhotoUrls.map((url, idx) => (
                  <button
                    key={idx}
                    onClick={() => { setLightboxUrl(url); setLightboxIndex(idx); }}
                    data-testid={`drawer-packing-photo-${idx}`}
                    className="relative aspect-square rounded-lg overflow-hidden border border-slate-200 hover:opacity-80 transition-opacity"
                  >
                    <img src={url} alt={`Packing photo ${idx + 1}`} className="w-full h-full object-cover" />
                  </button>
                ))}
              </div>
            </Section>
          )}

          {/* Customer */}
          <Section icon={User} title="Customer Details" testid="drawer-customer-section">
            <Field label="Name" value={order.customer?.name} />
            <Field label="Email" value={order.customer?.email ? (<a href={`mailto:${order.customer.email}`} className="inline-flex items-center gap-1.5 text-welljio-accent hover:underline"><Mail size={12} /> {order.customer.email}</a>) : null} />
            <Field label="Phone" value={order.customer?.phone ? (<a href={`tel:${order.customer.phone}`} className="inline-flex items-center gap-1.5 text-welljio-accent hover:underline"><Phone size={12} /> {order.customer.phone}</a>) : null} />
          </Section>

          {/* Products */}
          <Section icon={Package} title={`Products (${items.length})`} testid="drawer-products-section">
            <ul className="divide-y divide-slate-100 -m-1">
              {items.map((it, i) => (
                <li key={i} className="flex items-center gap-3 py-3 px-1">
                  <div className="w-14 h-14 rounded-xl bg-white border border-slate-100 overflow-hidden shrink-0">
                    {it.image && <img src={it.image} alt={it.name} className="w-full h-full object-contain p-1.5" />}
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="font-medium text-welljio-ink truncate">{it.name}</p>
                    <p className="text-xs text-welljio-muted">{it.price !== undefined ? `${inr(it.price)} × ` : ""}{it.quantity}</p>
                  </div>
                  {it.line_total !== undefined && <p className="font-semibold text-welljio-ink whitespace-nowrap">{inr(it.line_total)}</p>}
                </li>
              ))}
            </ul>
            {order.total !== undefined && (
              <div className="mt-3 pt-3 border-t border-slate-100 space-y-1.5 text-sm">
                <div className="flex justify-between text-welljio-muted"><span>Subtotal</span><span>{inr(order.subtotal || 0)}</span></div>
                <div className="flex justify-between text-welljio-muted"><span>Shipping</span><span>{order.shipping ? inr(order.shipping) : "Free"}</span></div>
                <div className="flex justify-between font-semibold text-welljio-ink pt-1.5 border-t border-slate-100"><span>Total</span><span data-testid="drawer-total">{inr(order.total)}</span></div>
              </div>
            )}
          </Section>

          {/* Shipping Address */}
          <Section
            icon={MapPin}
            title="Shipping Address"
            testid="drawer-address-section"
            action={canManageOrder && !editingAddress && (
              <button onClick={openAddressEdit} data-testid="drawer-edit-address-button" className="inline-flex items-center gap-1.5 text-xs text-welljio-accent hover:underline">
                <Pencil size={12} /> Edit
              </button>
            )}
          >
            {editingAddress ? (
              <div className="space-y-2">
                <input value={addressForm.address_line1} onChange={(e) => setAddressForm({ ...addressForm, address_line1: e.target.value })} placeholder="Address line 1" data-testid="address-edit-line1" className="w-full border border-slate-200 rounded-xl px-3.5 py-2.5 text-sm outline-none focus:border-welljio-accent" />
                <input value={addressForm.address_line2} onChange={(e) => setAddressForm({ ...addressForm, address_line2: e.target.value })} placeholder="Address line 2 (optional)" data-testid="address-edit-line2" className="w-full border border-slate-200 rounded-xl px-3.5 py-2.5 text-sm outline-none focus:border-welljio-accent" />
                <div className="grid grid-cols-3 gap-2">
                  <input value={addressForm.city} onChange={(e) => setAddressForm({ ...addressForm, city: e.target.value })} placeholder="City" data-testid="address-edit-city" className="border border-slate-200 rounded-xl px-3.5 py-2.5 text-sm outline-none focus:border-welljio-accent" />
                  <input value={addressForm.state} onChange={(e) => setAddressForm({ ...addressForm, state: e.target.value })} placeholder="State" data-testid="address-edit-state" className="border border-slate-200 rounded-xl px-3.5 py-2.5 text-sm outline-none focus:border-welljio-accent" />
                  <input value={addressForm.pincode} onChange={(e) => setAddressForm({ ...addressForm, pincode: e.target.value })} placeholder="Pincode" data-testid="address-edit-pincode" className="border border-slate-200 rounded-xl px-3.5 py-2.5 text-sm outline-none focus:border-welljio-accent" />
                </div>
                <VerifyFields verifiedVia={addressVerifiedVia} setVerifiedVia={setAddressVerifiedVia} reason={addressReason} setReason={setAddressReason} contactHint={contactHint} testidPrefix="address-edit" />
                <div className="flex items-center gap-2 pt-1">
                  <button onClick={saveAddress} disabled={savingAddress} data-testid="address-edit-save" className="bg-welljio-ink text-white rounded-full px-4 py-2 text-xs font-medium hover:bg-slate-800 disabled:opacity-60">{savingAddress ? "Saving..." : "Save address"}</button>
                  <button onClick={() => setEditingAddress(false)} className="text-xs text-welljio-muted px-3">Cancel</button>
                </div>
              </div>
            ) : (addr.line1 || addr.city) ? (
              <address className="not-italic leading-relaxed text-welljio-ink">
                <p className="font-medium">{order.customer?.name}</p>
                <p>{addr.line1}</p>
                {addr.line2 && <p>{addr.line2}</p>}
                <p>{[addr.city, addr.state, addr.pincode].filter(Boolean).join(", ")}</p>
                {order.customer?.phone && <p className="mt-2 text-xs text-welljio-muted">Contact: {order.customer.phone}</p>}
              </address>
            ) : (
              <p className="text-welljio-muted">No shipping address on file.</p>
            )}
          </Section>

          {/* Payment */}
          <Section icon={CreditCard} title="Payment Information" testid="drawer-payment-section">
            <Field label="Method" value={order.payment_method === "cod" ? "Cash on Delivery" : "Razorpay"} />
            <Field label="Payment Status" value={PAYMENT_LABEL[pay]} />
            {order.razorpay_order_id && <Field label="Razorpay Order" value={<code className="text-xs font-mono">{order.razorpay_order_id}</code>} />}
            {order.razorpay_payment_id && <Field label="Payment ID" value={<code className="text-xs font-mono">{order.razorpay_payment_id}</code>} />}
            {order.paid_at && <Field label="Paid On" value={fmtDate(order.paid_at)} />}
            {order.total !== undefined && <Field label="Amount" value={<span className="font-semibold">{inr(order.total)} {order.currency || "INR"}</span>} />}
            {refundedAmount > 0 && <Field label="Refunded" value={<span className="text-rose-600 font-medium">{inr(refundedAmount)}</span>} />}
          </Section>

          {/* Refund */}
          {canManageOrder && order.status === "paid" && refundRemaining > 0 && (
            <Section
              icon={RotateCcw}
              title="Refund"
              testid="drawer-refund-section"
              action={!showRefundForm && (
                <button onClick={() => { setShowRefundForm(true); setRefundVerifiedVia(""); setRefundReason(""); setRefundAmount(""); }} data-testid="drawer-open-refund-button" className="inline-flex items-center gap-1.5 text-xs text-welljio-accent hover:underline">
                  Issue refund
                </button>
              )}
            >
              {showRefundForm ? (
                <div className="space-y-2">
                  <p className="text-xs text-welljio-muted">Up to {inr(refundRemaining)} available to refund. Leave amount blank for a full refund.</p>
                  <input type="number" value={refundAmount} onChange={(e) => setRefundAmount(e.target.value)} placeholder={`Amount (max ${refundRemaining})`} data-testid="refund-amount-input" className="w-full border border-slate-200 rounded-xl px-3.5 py-2.5 text-sm outline-none focus:border-welljio-accent" />
                  <VerifyFields verifiedVia={refundVerifiedVia} setVerifiedVia={setRefundVerifiedVia} reason={refundReason} setReason={setRefundReason} contactHint={contactHint} testidPrefix="refund" />
                  <div className="flex items-center gap-2 pt-1">
                    <button onClick={submitRefund} disabled={refunding} data-testid="refund-submit" className="bg-red-600 text-white rounded-full px-4 py-2 text-xs font-medium hover:bg-red-700 disabled:opacity-60">{refunding ? "Processing..." : "Confirm refund"}</button>
                    <button onClick={() => setShowRefundForm(false)} className="text-xs text-welljio-muted px-3">Cancel</button>
                  </div>
                </div>
              ) : (
                <p className="text-welljio-muted">{inr(refundRemaining)} available to refund on this order.</p>
              )}
            </Section>
          )}

          {/* Order Timeline */}
          {history.length > 0 && (
            <Section icon={Clock} title="Order Timeline" testid="drawer-timeline-section">
              <ol className="relative border-l border-slate-200 ml-2 space-y-4">
                {history.map((h, i) => (
                  <li key={i} className="ml-4">
                    <span className="absolute -left-1.5 flex items-center justify-center w-3 h-3 rounded-full bg-welljio-accent ring-4 ring-welljio-tint/60" />
                    <p className="text-sm font-medium text-welljio-ink">{h.label || h.status}</p>
                    {h.actor_name && <p className="text-xs text-welljio-accent">{h.actor_name}</p>}
                    <p className="text-xs text-welljio-muted">{fmtDate(h.timestamp)}</p>
                  </li>
                ))}
              </ol>
              {order.estimated_delivery && (
                <div className="mt-4 flex items-center gap-2 text-xs text-welljio-muted">
                  <ShieldCheck size={13} /> Estimated delivery: {fmtDate(order.estimated_delivery)}
                </div>
              )}
            </Section>
          )}

          {/* Activity Log */}
          {canManageOrder && (
            <Section icon={History} title="Activity Log" testid="drawer-activity-log-section">
              {loadingLog ? (
                <p className="text-welljio-muted">Loading...</p>
              ) : activityLog.length === 0 ? (
                <p className="text-welljio-muted">No edits, cancellations, or refunds on this order.</p>
              ) : (
                <ul className="space-y-3">
                  {activityLog.map((entry) => (
                    <li key={entry.id} data-testid={`activity-log-entry-${entry.id}`} className="border-b border-slate-100 last:border-0 pb-3 last:pb-0">
                      <div className="flex items-center justify-between">
                        <p className="font-medium text-welljio-ink">{ACTION_LABEL[entry.action] || entry.action}</p>
                        <p className="text-xs text-welljio-muted">{fmtDate(entry.created_at)}</p>
                      </div>
                      <p className="text-xs text-welljio-muted mt-0.5">{entry.actor_name || entry.actor_email} · {entry.actor_role} · verified via {entry.verified_via}</p>
                      {entry.reason && <p className="text-xs text-welljio-ink mt-1">"{entry.reason}"</p>}
                    </li>
                  ))}
                </ul>
              )}
            </Section>
          )}
        </div>
      </SheetContent>

      {lightboxUrl && createPortal(
        <div
          onClick={() => setLightboxUrl(null)}
          onPointerDown={(e) => e.stopPropagation()}
          data-testid="drawer-photo-lightbox"
          style={{ pointerEvents: "auto" }}
          className="fixed inset-0 z-[100] bg-black/80 flex items-center justify-center p-6"
        >
          <img src={lightboxUrl} alt="Packing photo" onClick={(e) => e.stopPropagation()} className="max-w-full max-h-full rounded-xl" />
          <button
            type="button"
            onClick={(e) => { e.stopPropagation(); downloadPhoto(lightboxUrl, lightboxIndex); }}
            data-testid="drawer-photo-download"
            className="absolute bottom-6 right-6 bg-white text-welljio-ink rounded-full px-4 py-2 text-xs font-medium hover:bg-slate-100"
          >
            Download
          </button>
          <button
            type="button"
            onClick={(e) => { e.stopPropagation(); setLightboxUrl(null); }}
            aria-label="Close"
            data-testid="drawer-photo-lightbox-close"
            className="absolute top-6 right-6 w-9 h-9 flex items-center justify-center rounded-full bg-white/10 text-white text-xl leading-none hover:bg-white/20"
          >
            ×
          </button>
        </div>,
        document.body
      )}
    </Sheet>
  );
}
